import { Avatar, Box, DialogContent, Divider, Modal } from "@mui/material"
import { SxProps } from "@mui/system"
import {
  FocusContext,
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, {
  Dispatch,
  memo,
  MutableRefObject,
  ReactNode,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Outlet, useParams } from "react-router"
import Atmos from "src/components/images/atmos.png"

import { LiveConcertCountdown } from "../../components/Banner/LiveConcertCountdown"
import { ContentLabels } from "../../components/common/ContentLabels"
import { DGText } from "../../components/common/DGText"
import { GradientOverlay } from "../../components/common/GradientOverlay/GradientOverlay"
import { Image } from "../../components/common/Image"
import { LiveConcertTime } from "../../components/common/LiveConcertTime"
import FourK from "../../components/images/4k.png"
import empty from "../../components/images/empty.png"
import {
  Sliders,
  SliderType,
  TeaserDimension,
} from "../../components/Sliders/Slider"
import { useAccessCheck } from "../../hooks/use-access-check"
import useIntl from "../../hooks/use-intl"
import useSdk, {
  CuratedContentContainerV2,
  ErrorLevel,
  SliderLayout,
} from "../../hooks/use-sdk"
import { CuratedContentExtract, useSliders } from "../../hooks/use-sliders"
import { useSmoothNavigate } from "../../hooks/use-smooth-navigate"
import { useTransitions } from "../../hooks/use-transitions"
import useTranslate from "../../hooks/use-translate"
import { appStore } from "../../store/AppStore"
import { PlaybackContent, playerStore } from "../../store/PlayerStore"
import { comb, s, secondsToMinutes } from "../../utilities/common"
import { fontSizeFactor, fontSizeFactorStep } from "../../utilities/constants"
import { theme, TI } from "../../utilities/theme"
import { useFocusHelper } from "../../utilities/use-focus-helper"
import { useRouteMatch } from "../../utilities/use-route-match"
import { LoginModal } from "../Settings/Login/LoginModal"
import { SubscribeModal } from "../Settings/Subscribe/SubscribeModal"

import { Buttons } from "./components/Buttons"
import { CategoryLinks } from "./components/CategoryLinks"
import { EditorialPlaylist } from "./components/EditorialPlaylist/EditorialPlaylist"
import { FocusedSliders } from "./components/FocusedSliders"
import { useCredits } from "./components/InfoModal/common"
import { InfoModal } from "./components/InfoModal/InfoModal"
import { Playlist } from "./components/Playlist"
import { Reruns } from "./components/Reruns"
import {
  createStore,
  DetailPage,
  DetailpageData,
  Provider,
  useDetailStore,
} from "./DetailStore"
import { useContentTracking } from "./use-content-tracking"
import {
  convertTypeToWebSlug,
  usePageviewTracking,
} from "./use-pageview-tracking"

export const mapSliderType = (typename: string) =>
  typename === "Work" ? SliderType.Collection : SliderType.Vertical

// check layout and use typename as fallback
export const mapTeaserDimensions = (typename: string, layout?: SliderLayout) =>
  layout && layout === SliderLayout.WeeklyTop10
    ? TeaserDimension.WeeklyTop10
    : typename === "LiveConcert"
      ? TeaserDimension.LandscapeLargeTeaser
      : typename === "VodConcert" || typename === "Video"
        ? TeaserDimension.LandscapeSmallTeaser
        : typename === "Album" || typename === "EditorialPlaylist"
          ? TeaserDimension.SquareSmallTeaser
          : typename === "Artist" || typename === "Group"
            ? TeaserDimension.AvatarBox
            : typename === "Partner"
              ? TeaserDimension.AvatarLabel
              : typename === "Epoch" ||
                  typename === "Genre" ||
                  typename === "CuratedContentLink"
                ? TeaserDimension.LandscapeSmallTeaserNoText
                : undefined

export type QuerySlider = Extract<
  CuratedContentContainerV2,
  { __typename: "ListSlider" }
>

export const useLabels = (data?: DetailpageData): string[] => {
  const t = useTranslate()
  const { dateFormat } = useIntl()
  return useMemo(() => {
    const result: string[] = []
    if (data === undefined) return result
    if ("productionDate" in data && data.productionDate)
      result.push(
        t("video__production_date", [
          dateFormat(new Date(data.productionDate), "short"),
        ]),
      )
    if (data.__typename === "VodConcert" && data.productionDate) {
      result.push(
        t("video__duration_in_minutes", [secondsToMinutes(data.totalDuration)]),
      )
    } else {
      if (data.__typename === "Video")
        result.push(
          t("video__duration_in_minutes", [secondsToMinutes(data.duration)]),
        )
      else if (data.__typename === "Album")
        result.push(
          t("video__duration_in_minutes", [
            secondsToMinutes(data.totalDuration),
          ]),
        )
      if ("takedownDate" in data && data.takedownDate)
        result.push(
          t("video__available_until_day", [
            dateFormat(new Date(data.takedownDate), "short"),
          ]),
        )
    }
    return result
  }, [data])
}

const Screen = ({
  page,
  sliders,
  resetSliders,
}: {
  page: DetailPage
  sliders?: Sliders
  resetSliders: () => void
}) => {
  const infoOpen = useDetailStore((state) => state.infoOpen)
  const {
    sliderIndex: sliderIndexString,
    teaserIndex: teaserIndexString,
    sliderId,
  } = useParams()

  const sliderIndex = useRef(0)
  const teaserIndex = useRef(0)
  useEffect(() => {
    if (sliderIndexString) sliderIndex.current = parseInt(sliderIndexString, 10)
    if (teaserIndexString) teaserIndex.current = parseInt(teaserIndexString, 10)
  }, [sliderIndexString, teaserIndexString])

  const scrollRef = useRef<HTMLElement>(null)
  const isMyLocation = useRouteMatch()

  const [loginOpen, setLoginOpen] = useState(false)
  const [subscribeOpen, setSubscribeOpen] = useState(false)
  const fk = useRef("")
  const setSpinner = appStore((state) => state.setSpinner)
  const { open: playerOpen } = playerStore((state) => state.navigationProps)
  const setContentPlayer = playerStore((state) => state.setContent)
  const setNavigationProps = playerStore((state) => state.setNavigationProps)
  const setReturnFocus = playerStore((state) => state.setReturnFocus)

  const { addFocus, getFocus } = useFocusHelper()
  const { ref, focusKey } = useFocusable({
    isFocusBoundary: true,
  })
  const content = useDetailStore((state) => state.content)
  const t = useTranslate()

  // custom page_types defined in DGAPP-4590
  const pageType = useMemo(() => {
    if (!content) return undefined
    return content.__typename === "VodConcert" ||
      content.__typename === "Video" ||
      content.__typename === "LiveConcert"
      ? "VideoDetail"
      : content.__typename === "Album"
        ? "AudioDetail"
        : content.__typename === "EditorialPlaylist"
          ? "PlaylistDetail"
          : content.__typename
  }, [content])

  const trackDetailPage = useContentTracking({ content, pageType })
  const trackPageview = usePageviewTracking()
  const slug = appStore((state) => state.slug)
  const transition = useTransitions()

  useLayoutEffect(() => {
    setSpinner("in")
    if (playerOpen || infoOpen || loginOpen || subscribeOpen) setSpinner("out")
    if (page && isMyLocation) {
      setTimeout(() => {
        getFocus()
        setSpinner("out")
      }, 200)
    }
  }, [page, isMyLocation])

  useEffect(() => {
    if (content) {
      trackDetailPage(
        sliderIndex.current // if not zero
          ? {
              slider_position: sliderIndex.current,
              slider_id: sliderId,
              content_teaser_position: teaserIndex.current,
            }
          : undefined,
      )
      trackPageview(
        pageType,
        `${convertTypeToWebSlug(content.__typename)}/${content.id}`,
      )
    }
  }, [
    page,
    isMyLocation,
    slug,
    content,
    pageType,
    sliderIndex,
    sliderId,
    teaserIndex,
  ])

  return (
    <>
      <Box
        style={{ transition }}
        sx={{
          //opacity: visible,
          position: "relative",
          width: "100%",
          minHeight: "100vh",
          overflow: "hidden",
          backgroundColor: theme.palette.primary.main,
        }}
        ref={scrollRef}
        className={"loading_in"}
      >
        <FocusContext.Provider value={focusKey}>
          <Box ref={ref} sx={{ width: "100%", pb: s("300px") }}>
            <MemoizedScreen
              resetSliders={resetSliders}
              setLoginOpen={setLoginOpen}
              loginOpen={loginOpen}
              setSubscribeOpen={setSubscribeOpen}
              subscribeOpen={subscribeOpen}
              fk={fk}
              scrollRef={scrollRef}
              sliders={sliders}
              addFocus={addFocus}
            />
          </Box>
        </FocusContext.Provider>
      </Box>
      {/* we are using a different instance of InfoModal on EditorialPlaylists */}
      {content?.__typename !== "EditorialPlaylist" && (
        <InfoModal open={infoOpen} />
      )}
      <LoginModal
        titleOverride={
          page.publicationLevel === "ACCOUNT_REQUIRED"
            ? t("smarttv__login_title_account_required")
            : undefined
        }
        isOpen={loginOpen}
        close={() => {
          setLoginOpen(false)
          setFocus(fk.current)
        }}
      />
      <SubscribeModal
        isOpen={subscribeOpen}
        onSuccess={() => {
          setSubscribeOpen(false)
          setReturnFocus(fk.current)
          setContentPlayer(content as PlaybackContent, "0", false)
          setNavigationProps({
            open: true,
            id: page.id,
            type: page.type,
            isPreview: "false",
            trackId: "0",
            startFromProgress: true,
          })
        }}
        close={() => {
          setSubscribeOpen(false)
          setFocus(fk.current)
        }}
      />
      <Outlet />
    </>
  )
}

const MemoizedScreen = memo(
  ({
    resetSliders,
    setLoginOpen,
    loginOpen,
    setSubscribeOpen,
    subscribeOpen,
    fk,
    scrollRef,
    sliders,
    addFocus,
  }: {
    resetSliders: () => void
    setLoginOpen: Dispatch<SetStateAction<boolean>>
    loginOpen: boolean
    setSubscribeOpen: Dispatch<SetStateAction<boolean>>
    subscribeOpen: boolean
    fk: MutableRefObject<string>
    scrollRef: RefObject<HTMLElement | null>
    sliders?: Sliders
    addFocus: () => void
  }) => {
    const content = useDetailStore((state) => state.content)
    const labels = useLabels(content)
    const page = useDetailStore((state) => state.page)

    const { accessCheck: accessCheckRaw } = useAccessCheck({
      loginOpen: comb.ine(loginOpen, setLoginOpen),
      subscribeOpen: comb.ine(subscribeOpen, setSubscribeOpen),
      focusKey: fk,
    })

    const accessCheck = useCallback(() => accessCheckRaw({ page }), [page])

    const navigate = useSmoothNavigate()

    const creditsRaw = useCredits(content)
    const t = useTranslate()

    const credits = useMemo(
      () =>
        creditsRaw &&
        creditsRaw.map((item, index) => (
          <React.Fragment key={"credits_" + index}>
            {`${t(item.titleTranslationKey)}:`}
            <br />
            {`${item.values.join(", ")}`}
            {index < creditsRaw.length - 1 && (
              <>
                <br />
                <br />
              </>
            )}
          </React.Fragment>
        )),
      [creditsRaw],
    )

    return page ? (
      <>
        <CategoryLinks addFocus={addFocus} page={page} />
        {content?.__typename === "Video" ||
        content?.__typename === "VodConcert" ||
        content?.__typename === "LiveConcert" ||
        content?.__typename === "Album" ? (
          <>
            <Image
              alt={""}
              src={page.backgroundImage}
              hasTransparentFallback
              sx={{
                position: "absolute",
                width: "100%",
                height: "100vh",
                top: 0,
                backgroundRepeat: "no-repeat",
                backgroundImage: `url(${empty})`,
                backgroundPosition: "center",
                backgroundSize: s("360px"),
                backgroundColor: theme.palette.secondary.main,
              }}
            />
            <GradientOverlay type={"full"} />
            {content.__typename === "LiveConcert" ? (
              <Box
                sx={{
                  position: "absolute",
                  left: s("471px"),
                  top: s("80px"),
                }}
              >
                <LiveConcertCountdown
                  liveConcert={content}
                  resetFn={resetSliders}
                />
              </Box>
            ) : null}
            <Box
              sx={{
                position: "absolute",
                width: s("405px"),
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                right: s("96px"),
                top: s("182px"),
                justifyContent: "flex-start",
              }}
            >
              <ContentLabels
                sx={{ mb: s("48px") }}
                publicationLevel={page.publicationLevel}
                content={content}
              />
              {page.type === "Album" ? (
                <Box sx={{ minHeight: s("320px"), mb: s("90px") }}>
                  <Image
                    width={s("320px")}
                    hasTransparentFallback
                    src={page.albumCover}
                  />
                </Box>
              ) : null}
              <Box
                sx={{
                  "& > :not(:last-child)": {
                    mb: s("24px"),
                  },
                }}
              >
                <Box
                  component={"span"}
                  sx={{
                    fontWeight: 600,
                    fontSize: s("24px", false, fontSizeFactor),
                    lineHeight: s("34px", false, fontSizeFactor),
                    color: theme.palette.white.main,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 24,
                  }}
                >
                  {credits}
                </Box>
              </Box>
              {page.icons.length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    mt: s("48px"),
                    "& > :not(:last-child)": {
                      mr: s("48px"),
                    },
                  }}
                >
                  {page.icons.includes("4k") && (
                    <Box
                      width={s("53px")}
                      height={s("36px")}
                      component={"img"}
                      src={FourK}
                    />
                  )}
                  {page.icons.includes("atmos") && (
                    <Box
                      width={s("97px")}
                      height={s("36px")}
                      component={"img"}
                      src={Atmos}
                    />
                  )}
                </Box>
              ) : null}
            </Box>
            <Box
              sx={{
                position: "relative",
                mt: content.__typename === "Album" ? s("240px") : s("182px"),
                ml: s("243px"),
                width: s("992px"),
                display: "flex",
                flexDirection: "column",
              }}
            >
              {
                content.__typename === "LiveConcert" ? (
                  <LiveConcertTime liveConcert={content} />
                ) : null /* TODO clarify if a yellow label is needed here */
              }
              <DGText
                tx={TI.H01tv}
                sx={{
                  color: "#FFFFFF",
                  textAlign: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  fontVariantNumeric: "oldstyle-nums",
                  fontFeatureSettings: "onum",
                  pb: s("1px"), // DGAPP-4979: Descenders get cut off
                }}
              >
                {page.title}
              </DGText>
              <DGText
                tx={TI.H04tv}
                sx={{
                  mt: s("8px"),
                  mb: s("48px"),
                  color: "#FFFFFF",
                  textAlign: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: fontSizeFactorStep > 2 ? 4 : 6,
                  fontVariantNumeric: "oldstyle-nums",
                  fontFeatureSettings: "onum",
                }}
              >
                {page.subtitle}
              </DGText>
              <Buttons
                hasCategoryLinks={!!page.links}
                accessCheck={accessCheck}
                scrollRef={scrollRef}
              />
              <Box
                sx={{
                  display: "flex",
                  mt: s("36px"),
                  mb: s("36px"), // imitating Android
                  pt: s("32px"),
                  pb: s("32px"),
                  flexDirection: "column",
                  alignItems: "center",
                  "& > :not(:last-child)": {
                    mb: s("32px"),
                  },
                }}
              >
                {labels.map((text, i) => (
                  <DGText
                    tx={TI.Subheadline02tv}
                    key={page.id + "subtitles" + i}
                    sx={{
                      color: theme.palette.white[55],
                    }}
                  >
                    {text}
                  </DGText>
                ))}
                {content.__typename === "LiveConcert" ? (
                  <>
                    <Reruns liveConcert={content} />
                    <Box sx={{ height: s("114px") }} />
                  </>
                ) : null}
              </Box>
            </Box>
          </>
        ) : content?.__typename === "Partner" ||
          content?.__typename === "Artist" ||
          content?.__typename === "Genre" ||
          content?.__typename === "Epoch" ||
          content?.__typename === "Group" ? (
          <>
            {page.backgroundImage ? (
              <Image
                src={page.backgroundImage}
                sx={{
                  position: "absolute",
                  width: s("1242px"),
                  height: s("699px"),
                  top: 0,
                }}
              />
            ) : null}
            {page.backgroundImage ? <GradientOverlay type={"small"} /> : null}
            <Box sx={{ height: s("653px") }}>
              <Box
                sx={{
                  position: "absolute",
                  top: s("239px"),
                  left: page.backgroundImage ? s("830px") : 0,
                  display: "flex",
                  flexDirection: "column",
                  width: page.backgroundImage ? s("1010px") : "100%",
                  alignItems: "center",
                }}
              >
                <Box sx={{ width: s("1010px") }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      pb: s("50px"),
                    }}
                  >
                    {content.__typename === "Partner" && (
                      <Avatar
                        src={
                          content.pictures.filter((p) => p.type === "LOGO")[0]
                            .url
                        }
                        sx={{
                          height: s("148px"),
                          width: s("148px"),
                          mr: s("34px"),
                          backgroundRepeat: "no-repeat",
                          backgroundImage: `url(${empty})`,
                          backgroundPosition: "center",
                          backgroundSize: s("240px"),
                          backgroundColor: theme.palette.secondary.main,
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        alignItems:
                          content.__typename === "Partner"
                            ? "flex-start"
                            : "center",
                      }}
                    >
                      <DGText
                        tx={TI.H01tv}
                        sx={{
                          color: theme.palette.white.main,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 2,
                          textAlign:
                            content.__typename === "Partner"
                              ? "left"
                              : "center",
                          pb: s("1px"), // DGAPP-4979: Descenders get cut off
                        }}
                      >
                        {page.title}
                      </DGText>
                      <DGText
                        tx={TI.H07tv}
                        sx={{
                          mt: s("4px"),
                          color: theme.palette.white.main,
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: page.backgroundImage ? 3 : 1,
                        }}
                      >
                        {page.subtitle}
                      </DGText>
                      {content.__typename === "Artist" &&
                      content.dateOfBirth &&
                      content.dateOfDeath ? (
                        <DGText
                          tx={TI.Subheadline05tv}
                          sx={{
                            mt: s("32px"),
                            color: theme.palette.white[70],
                          }}
                        >
                          {content.dateOfBirth} - {content.dateOfDeath}
                        </DGText>
                      ) : null}
                    </Box>
                  </Box>
                  <Buttons
                    hasCategoryLinks={!!page.links}
                    accessCheck={accessCheck}
                    scrollRef={scrollRef}
                  />
                </Box>
              </Box>
            </Box>
          </>
        ) : content?.__typename === "Work" ? (
          <Box
            sx={{
              height: s("600px"),
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <FocusableHeader
              scrollRef={scrollRef}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                width: s("1010px"),
                alignItems: "center",
              }}
            >
              <DGText
                tx={TI.H01tv}
                sx={{
                  color: theme.palette.white.main,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 3,
                  textAlign: "center",
                  pb: s("1px"), // DGAPP-4979: Descenders get cut off
                }}
              >
                {page.title}
              </DGText>
              <DGText
                tx={TI.H07tv}
                sx={{
                  mt: s("8px"),
                  color: theme.palette.white.main,
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                }}
              >
                {page.subtitle}
              </DGText>
            </FocusableHeader>
          </Box>
        ) : content?.__typename === "EditorialPlaylist" ? (
          <EditorialPlaylist
            fk={fk}
            subscribeOpen={comb.ine(subscribeOpen, setSubscribeOpen)}
          />
        ) : null}
        <>
          {content?.__typename === "Video" ||
          content?.__typename === "VodConcert" ||
          content?.__typename === "LiveConcert" ? (
            <Divider
              sx={{
                borderColor: theme.palette.white[20],
                width: s("846px"),
              }}
            />
          ) : null}
          {content?.__typename !== "EditorialPlaylist" && (
            <Playlist
              scrollRef={scrollRef}
              subscribeOpen={comb.ine(subscribeOpen, setSubscribeOpen)}
              loginOpen={comb.ine(loginOpen, setLoginOpen)}
              fk={fk}
            />
          )}
        </>
        <FocusedSliders
          marginTop={
            (content?.__typename === "Artist" ||
              content?.__typename === "Work" ||
              content?.__typename === "Group") &&
            !page.backgroundImage
              ? 0
              : s("124px")
          }
          sliders={sliders}
          addFocus={addFocus}
          navigate={navigate}
          scrollRef={scrollRef}
        />
      </>
    ) : null
  },
)

const FocusableHeader = ({
  scrollRef,
  children,
  sx,
}: {
  scrollRef: RefObject<HTMLElement | null>
  children: ReactNode
  sx: SxProps
}) => {
  const { ref, focusSelf } = useFocusable({
    onFocus: () => {
      if (scrollRef.current)
        scrollRef.current.style.transform = "translate3d(0, 0, 0)"
    },
  })

  useEffect(() => {
    focusSelf()
  }, [focusSelf])
  return (
    <Box tabIndex={0} sx={sx} ref={ref}>
      {children}
    </Box>
  )
}

const ScreenWrapper = () => {
  const { id, type } = useParams()
  const page = useDetailStore((state) => state.page)
  const setContent = useDetailStore((state) => state.setContent)
  const content = useDetailStore((state) => state.content)
  const sdk = useSdk(ErrorLevel.WithPrompt)

  const [tCuratedContent, setCuratedContent] =
    useState<CuratedContentExtract[]>()
  const currentUser = appStore((state) => state.currentUser)
  const setFavorites = useDetailStore((state) => state.setFavorites)
  const favorites = useDetailStore((state) => state.favorites)
  const sliders = useSliders(tCuratedContent)

  const resetSliders = useCallback(() => {
    if (!(id && type)) return
    if (type === "Video" || type === "VodConcert" || type === "LiveConcert") {
      ;(async () => {
        const tData = await sdk.videoDetailPage({ videoId: id })
        if (
          tData?.node?.__typename === "Video" ||
          tData?.node?.__typename === "VodConcert" ||
          tData?.node?.__typename === "LiveConcert"
        ) {
          setContent(tData.node)
          setCuratedContent(tData.node?.curatedContent)
        }
        if (type === "VodConcert") {
          if (currentUser) {
            const result = await sdk.favoritesByVodConcertId({
              vodConcertId: id,
            })
            const favs = result.favoritesByVodConcertId
              ?.map((f) => {
                if (f && f.content.__typename === "PerformanceWork") {
                  return f.content.id
                }
              })
              .filter((f): f is string => !!f)
            setFavorites({ content: undefined, vodConcert: favs ?? [] })
          }
        } else if (type === "Video") {
          if (currentUser) {
            const result = await sdk.favoriteByContentId({ contentId: id })
            const favorited =
              result?.favoriteByContentId?.content.__typename === "Video"
                ? !!result.favoriteByContentId.content.id
                : false
            setFavorites({ content: favorited, vodConcert: undefined })
          }
        }
      })()
    } else if (type === "Album") {
      ;(async () => {
        const tData = await sdk.audioDetailPage({ albumId: id })
        setContent(tData.album)
        setCuratedContent(tData.album?.curatedContent)
        if (currentUser) {
          const result = await sdk.favoriteByContentId({ contentId: id })
          const favorited =
            result?.favoriteByContentId?.content.__typename === "Album"
              ? !!result.favoriteByContentId.content.id
              : false
          setFavorites({ content: favorited, vodConcert: undefined })
        }
      })()
    } else if (type === "Artist") {
      ;(async () => {
        const tData = await sdk.artistCollectionPage({ artistId: id })
        setContent(tData.artist)
        setCuratedContent(tData.artist?.curatedContent)
      })()
    } else if (type === "Group") {
      ;(async () => {
        const tData = await sdk.groupPage({ groupId: id })
        setContent(tData.group)
        setCuratedContent(tData.group?.curatedContent)
      })()
    } else if (type === "Partner") {
      ;(async () => {
        const tData = await sdk.partnerPage({ partnerId: id })
        setContent(tData.partner)
        setCuratedContent(tData.partner?.curatedContent)
      })()
    } else if (type === "Epoch") {
      ;(async () => {
        const tData = await sdk.epochPage({ epochId: id })
        setContent(tData.epoch)
        setCuratedContent(tData.epoch?.curatedContent)
      })()
    } else if (type === "Genre") {
      ;(async () => {
        const tData = await sdk.genrePage({ genreId: id })
        setContent(tData.genre)
        setCuratedContent(tData.genre?.curatedContent)
      })()
    } else if (type === "Work") {
      ;(async () => {
        const tData = await sdk.workPage({ workId: id })
        setContent(tData.work)
        setCuratedContent(tData.work?.curatedContent)
      })()
    } else if (type === "EditorialPlaylist") {
      ;(async () => {
        const tData = await sdk.editorialPlaylistPage({
          editorialPlaylistId: id,
        })
        setContent(tData.editorialPlaylist)
        setCuratedContent(tData.editorialPlaylist?.curatedContent)
        if (currentUser) {
          const result = await sdk.favoriteByContentId({ contentId: id })
          const favorited =
            result?.favoriteByContentId?.content.__typename ===
            "EditorialPlaylist"
              ? !!result.favoriteByContentId.content.id
              : false
          setFavorites({ content: favorited, vodConcert: undefined })
        }
      })()
    }
  }, [id, type, currentUser])

  useEffect(() => {
    resetSliders()
  }, [id, type, currentUser])

  // make sure favorites are queried before showing the page
  if (
    currentUser &&
    (((content?.__typename === "Album" ||
      content?.__typename === "Video" ||
      content?.__typename === "EditorialPlaylist") &&
      favorites.content === undefined) ||
      (content?.__typename === "VodConcert" &&
        favorites.vodConcert === undefined))
  )
    return null

  if (page)
    return <Screen page={page} resetSliders={resetSliders} sliders={sliders} />
  return null
}

export const DetailScreen = () => {
  const { id } = useParams()

  return (
    <Modal
      key={"detailscreen" + id}
      open={true}
      sx={{
        height: "100vh",
        width: "100vw",
        backgroundColor: theme.palette.primary.main,
      }}
    >
      <DialogContent
        sx={{
          height: "100%",
          width: "100%",
          backgroundColor: theme.palette.primary.main,
        }}
      >
        <Provider createStore={createStore}>
          <ScreenWrapper />
        </Provider>
      </DialogContent>
    </Modal>
  )
}
